import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/base/Navbar";
import Footer from "@/components/feature/Footer";
import { seoArticles } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://acbuy.yachts";

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${siteUrl}/article/${article.slug}#article`,
    "headline": article.title,
    "description": article.intro,
    "image": article.heroImage,
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "author": {
      "@type": "Organization",
      "name": "ACBuy Spreadsheet",
      "url": `${siteUrl}/`
    },
    "publisher": {
      "@type": "Organization",
      "name": "ACBuy Spreadsheet",
      "url": `${siteUrl}/`
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/article/${article.slug}`
    },
    "inLanguage": "en",
    "articleSection": article.category,
    "keywords": ["acbuyspreadsheet", "acbuy spreadsheet", "spreadsheet product tracking", article.category.toLowerCase()]
  };

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full h-[420px] md:h-[550px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.heroImage}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-background-50/80 hover:text-background-50 transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 leading-[1.1] mb-4 max-w-3xl">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-background-50/70">
            <span className="px-3 py-1 bg-background-50/20 backdrop-blur-sm rounded-full text-xs font-medium whitespace-nowrap">
              {article.category}
            </span>
            <span>{article.readTime}</span>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 pb-6 md:pb-10">
          <p className="font-heading text-3xl md:text-6xl text-background-50/12 text-center tracking-widest select-none">
            {article.category.toUpperCase()}
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-14 md:py-20 px-4 md:px-12 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
            {/* Side image */}
            <div className="lg:w-2/5">
              <div className="sticky top-24">
                <span className="inline-block px-4 py-2 border border-secondary-400 text-secondary-700 text-xs font-medium rounded-full mb-5 whitespace-nowrap">
                  {article.category}
                </span>
                <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src={article.sideImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Article body */}
            <div className="lg:w-3/5">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10">
                {article.intro}
              </p>

              <div className="space-y-10">
                {article.sections.map((section, idx) => (
                  <section key={idx}>
                    <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4 leading-snug">
                      {section.heading}
                    </h3>
                    <p
                      className="text-sm md:text-base text-foreground-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: section.body }}
                    />
                    {idx === 1 && (
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4">
                        For a complete overview of all available categories and tools, visit the{" "}
                        <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
                          ACBuySpreadsheet home page
                        </Link>.
                      </p>
                    )}
                  </section>
                ))}
              </div>

              {/* Article CTA */}
              <div className="mt-12 p-8 md:p-10 bg-primary-50 rounded-2xl border border-primary-100">
                <p className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
                  {article.ctaText}
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {article.ctaLabel}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>

              {/* Internal links */}
              <div className="mt-10 p-6 bg-background-100 rounded-2xl">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">
                  More ACBuySpreadsheet Resources
                </p>
                <div className="flex flex-wrap gap-2">
                  {seoArticles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 4)
                    .map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}`}
                        className="px-4 py-2 text-xs font-medium text-foreground-600 hover:text-primary-600 bg-background-50 hover:bg-primary-50 rounded-full border border-background-200 hover:border-primary-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {a.category}: {a.title.length > 40 ? a.title.slice(0, 40) + "..." : a.title}
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Bottom CTA */}
      <section className="py-14 md:py-20 px-4 md:px-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-0">
            <div className="lg:w-1/2 relative h-[300px] md:h-[400px] rounded-2xl overflow-hidden">
              <img
                src={article.sideImage}
                alt="Start using ACBuySpreadsheet"
                title="Get Started with ACBuySpreadsheet"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              <span className="absolute top-6 left-6 px-4 py-2 bg-background-50/80 backdrop-blur-sm text-foreground-800 text-xs font-medium rounded-full whitespace-nowrap">
                {article.category}
              </span>
            </div>
            <div className="lg:w-1/2 lg:pl-10 xl:pl-14 flex flex-col justify-center mt-8 lg:mt-0">
              <h3 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight mb-4">
                Start Using<br />ACBuySpreadsheet Today
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-md">
                Join thousands of smart shoppers who have upgraded their product research workflow. Centralize, compare, and buy with confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-foreground-300 text-foreground-800 hover:text-foreground-950 hover:border-foreground-500 font-medium text-sm rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Explore All Categories
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}